import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import {
  SaveIcon,
  FileTextIcon,
  UserIcon,
  CalendarIcon,
  DollarSignIcon,
  MapPinIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { SERVICE_TYPE_OPTIONS } from '../lib/serviceTypeOptions'
import {
  ActivityTypes,
  logActivity,
  getEntityActivities,
  formatActivityMeta,
  type ActivityLog,
} from '../utils/activityLogger'
import { JobAssignmentFields } from '../components/job/JobAssignmentFields'
import { JobTaskChecklist } from '../components/job/JobTaskChecklist'
import { ApartmentJobWork } from '../components/job/ApartmentJobWork'
import { JobGallery } from '../components/job/JobGallery'
import { JobCommentsThread } from '../components/job/JobCommentsThread'
import { JobChatPanel } from '../components/job/JobChatPanel'
import { JobQrRequirementsPicker } from '../components/job/JobQrRequirementsPicker'
import { JobCompletionModal } from '../components/job/JobCompletionModal'
import {
  filterStatusTransitionsForPermissions,
  formatStatusLabel,
  getAllowedStatusTransitions,
  isReviewWorkflowStatus,
  resolvePostCompletionStatus,
} from '../lib/jobStatusWorkflow'
import { useAppSettings } from '../hooks/useAppSettings'
import { jobMeetsAllPhotoRequirements } from '../lib/jobPhotoPolicy'
import { formatUnitTypeLabel } from '../lib/unitTypeHelpers'
import { usePermissions } from '../hooks/usePermissions'
import { fetchFieldJobLineItems } from '../api/fieldJobAccess'

interface Job {
  id: string
  job_number: string
  title: string
  description?: string
  service_type?: string
  scheduled_date?: string
  scheduled_start_time?: string
  scheduled_end_time?: string
  estimated_duration?: number
  status:
    | 'draft'
    | 'scheduled'
    | 'in_progress'
    | 'completed'
    | 'cancelled'
    | 'pending_review'
    | 'under_review'
    | 'qc_scheduled'
    | 'qc_passed'
    | 'qc_failed'
    | 'approved'
  priority: 'low' | 'medium' | 'high'
  quoted_amount?: number
  actual_amount?: number
  customer_id: string
  property_id: string
  proposal_id?: string
  contract_id?: string
  is_recurring?: boolean
  recurrence_type?: string
  recurrence_days?: number[]
  dsny_integration?: boolean
  dsny_pickup_days?: string[]
  notes?: string
  visit_group_id?: string | null
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
  tags?: string[] | null
  billing_type?: string | null
  actual_start_time?: string | null
  actual_end_time?: string | null
  created_at: string
  updated_at: string
  customer?: {
    company_name?: string
    contact_first_name: string
    contact_last_name: string
  }
  property?: {
    name: string
    address: string
    city?: string
    state?: string
  }
  proposal?: { proposal_number?: string; title?: string } | null
  contract?: { contract_number?: string; title?: string } | null
}

interface JobLineItem {
  id: string
  description: string
  quantity: number
  unit_type?: string
  unit_price: number
  total_price: number
  is_variable?: boolean
  billing_status?: string
}

type EditTab =
  | 'overview'
  | 'work'
  | 'line_items'
  | 'gallery'
  | 'notes'
  | 'chat'
  | 'activity'

export function JobEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [job, setJob] = useState<Job | null>(null)
  const [activeTab, setActiveTab] = useState<EditTab>('overview')
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [jobLineItems, setJobLineItems] = useState<JobLineItem[]>([])
  const [activities, setActivities] = useState<ActivityLog[]>([])
  const { photoPolicy } = useAppSettings()
  const { can, canSeePricing } = usePermissions()
  const canApproveReview = can('review.approve')
  const canCancelJob = can('jobs.cancel')

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    service_type: '',
    scheduled_date: '',
    scheduled_start_time: '',
    scheduled_end_time: '',
    estimated_duration: 0,
    status: 'draft' as Job['status'],
    priority: 'medium' as 'low' | 'medium' | 'high',
    quoted_amount: 0,
    actual_amount: 0,
    notes: '',
    tags: '',
  })

  useEffect(() => {
    if (id) {
      fetchJob(id)
    }
  }, [id, canSeePricing])

  const fetchJob = async (jobId: string) => {
    try {
      setLoading(true)

      const { data: jobData, error: jobError } = await supabase
        .from('jobs')
        .select(
          `
          *,
          customer:customers(id, company_name, contact_first_name, contact_last_name),
          property:properties(id, name, address, city, state),
          proposal:proposals(id, title, proposal_number),
          contract:contracts!jobs_contract_id_fkey(id, title, contract_number)
        `
        )
        .eq('id', jobId)
        .single()

      if (jobError) throw jobError

      setJob(jobData)
      setFormData({
        title: jobData.title || '',
        description: jobData.description || '',
        service_type: jobData.service_type || '',
        scheduled_date: jobData.scheduled_date || '',
        scheduled_start_time: jobData.scheduled_start_time || '',
        scheduled_end_time: jobData.scheduled_end_time || '',
        estimated_duration: jobData.estimated_duration || 0,
        status: jobData.status || 'draft',
        priority: jobData.priority || 'medium',
        quoted_amount: jobData.quoted_amount || 0,
        actual_amount: jobData.actual_amount || 0,
        notes: jobData.notes || '',
        tags: (jobData.tags || []).join(', '),
      })

      if (canSeePricing) {
        const { data: lineItemRows } = await supabase
          .from('job_line_items')
          .select(
            'id, description, quantity, unit_type, unit_price, total_price, is_variable, billing_status'
          )
          .eq('job_id', jobId)
          .order('sort_order', { ascending: true })
        setJobLineItems((lineItemRows as JobLineItem[]) || [])
      } else {
        const fieldLines = await fetchFieldJobLineItems(jobId)
        setJobLineItems(
          fieldLines.map((l) => ({
            id: l.id,
            description: l.description,
            quantity: Number(l.quantity ?? l.actual_quantity ?? 0),
            unit_type: l.unit_type ?? undefined,
            unit_price: 0,
            total_price: 0,
            is_variable: l.is_variable ?? undefined,
            billing_status: undefined,
          }))
        )
      }

      const activityData = await getEntityActivities('job', jobId)
      setActivities(activityData)
    } catch (error) {
      console.error('Error fetching job:', error)
      alert('Error loading job details')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !job) return

    if (formData.status === 'completed' && job.status !== 'completed') {
      const photoCheck = await jobMeetsAllPhotoRequirements(id, photoPolicy)
      if (!photoCheck.ok) {
        alert(photoCheck.message)
        setActiveTab(
          photoCheck.missingTaskLabels && photoCheck.missingTaskLabels.length > 0
            ? 'work'
            : 'gallery'
        )
        return
      }
      setShowCompletionModal(true)
      return
    }

    await saveJob()
  }

  const saveJob = async () => {
    if (!id || !job) return
    if (
      !canApproveReview &&
      formData.status !== job.status &&
      isReviewWorkflowStatus(formData.status)
    ) {
      alert('You do not have permission to set review or QC statuses.')
      return
    }
    if (
      !canCancelJob &&
      formData.status !== job.status &&
      (formData.status === 'cancelled' || job.status === 'cancelled')
    ) {
      alert('You do not have permission to cancel jobs.')
      return
    }
    setSaving(true)

    try {
      const oldStatus = job?.status
      const persistedStatus = resolvePostCompletionStatus(formData.status)
      const { error } = await supabase
        .from('jobs')
        .update({
          title: formData.title,
          description: formData.description || null,
          service_type: formData.service_type || null,
          scheduled_date: formData.scheduled_date || null,
          scheduled_start_time: formData.scheduled_start_time || null,
          scheduled_end_time: formData.scheduled_end_time || null,
          estimated_duration: formData.estimated_duration || null,
          status: persistedStatus,
          priority: formData.priority,
          quoted_amount: formData.quoted_amount || null,
          actual_amount: formData.actual_amount || null,
          notes: formData.notes || null,
          tags: formData.tags
            ? formData.tags
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean)
            : [],
          updated_at: new Date().toISOString(),
          ...(formData.status === 'completed' && job.status !== 'completed'
            ? { actual_end_time: new Date().toISOString() }
            : {}),
          ...(formData.status === 'in_progress' && job.status !== 'in_progress'
            ? { actual_start_time: new Date().toISOString() }
            : {}),
        })
        .eq('id', id)

      if (error) throw error

      await logActivity({
        activity_type: ActivityTypes.JOB_UPDATED,
        entity_type: 'job',
        entity_id: id!,
        description: `Job ${job?.job_number} updated`,
        metadata: {
          old_status: oldStatus,
          new_status: persistedStatus,
        },
      })

      alert('Job updated successfully!')
      navigate(`/jobs/details/${id}`)
    } catch (error) {
      console.error('Error updating job:', error)
      alert('Error updating job')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-gray-600">Loading job details...</div>
          </div>
        </div>
      </div>
    )
  }

  if (!job) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Job not found</h3>
            <p className="text-gray-500 mb-6">The requested job could not be found.</p>
            <button
              onClick={() => navigate('/jobs')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Jobs
            </button>
          </div>
        </div>
      </div>
    )
  }

  const tabs: Array<[EditTab, string]> = [
    ['overview', 'Overview'],
    ['line_items', 'Line Items'],
    ['work', 'Work'],
    ['gallery', 'Gallery'],
    ['notes', 'Notes'],
    ['chat', 'Chat'],
    ['activity', 'Activity'],
  ]

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title="Edit Job"
        subtitle={`Job #${job.job_number}`}
        onBack={() => navigate(`/jobs/details/${id}`)}
        backLabel="Back to Job Details"
      />

      <div className={mobile.surface}>
          <div className="sticky top-[3.25rem] z-10 flex overflow-x-auto scrollbar-none border-b border-gray-200 bg-white/95 backdrop-blur-md">
            {tabs.map(([tab, label]) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="p-4 sm:p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileTextIcon className="h-4 w-4" />
                      Job Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Job Title *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Service Type
                        </label>
                        <select
                          value={formData.service_type}
                          onChange={(e) =>
                            setFormData({ ...formData, service_type: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select service type</option>
                          {SERVICE_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select
                          value={formData.status}
                          onChange={(e) =>
                            setFormData({ ...formData, status: e.target.value as Job['status'] })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value={formData.status}>
                            {formatStatusLabel(formData.status)} (current)
                          </option>
                          {filterStatusTransitionsForPermissions(
                            getAllowedStatusTransitions(job.status || formData.status),
                            canApproveReview,
                            canCancelJob
                          ).map((status) => (
                              <option key={status} value={status}>
                                {formatStatusLabel(status)}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Priority
                        </label>
                        <select
                          value={formData.priority}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              priority: e.target.value as 'low' | 'medium' | 'high',
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                        <input
                          type="text"
                          value={formData.tags}
                          onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                          placeholder="comma-separated"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4" /> Schedule
                      </h3>
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Scheduled Date
                          </label>
                          <input
                            type="date"
                            value={formData.scheduled_date}
                            onChange={(e) =>
                              setFormData({ ...formData, scheduled_date: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start Time
                            </label>
                            <input
                              type="time"
                              value={formData.scheduled_start_time}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  scheduled_start_time: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              End Time
                            </label>
                            <input
                              type="time"
                              value={formData.scheduled_end_time}
                              onChange={(e) =>
                                setFormData({ ...formData, scheduled_end_time: e.target.value })
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Estimated Duration (minutes)
                          </label>
                          <input
                            type="number"
                            step="30"
                            min="0"
                            value={formData.estimated_duration}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                estimated_duration: parseInt(e.target.value) || 0,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        {job.is_recurring && (
                          <p className="text-xs text-blue-700">
                            Recurring {job.recurrence_type}
                            {job.recurrence_days?.length
                              ? ` · days ${job.recurrence_days.join(', ')}`
                              : ''}
                          </p>
                        )}
                        {job.dsny_integration && (
                          <p className="text-xs text-green-700">
                            DSNY
                            {job.dsny_pickup_days?.length
                              ? ` · ${job.dsny_pickup_days.join(', ')}`
                              : ''}
                          </p>
                        )}
                        {(job.actual_start_time || job.actual_end_time) && (
                          <p className="text-xs text-gray-500">
                            Actual:{' '}
                            {job.actual_start_time
                              ? new Date(job.actual_start_time).toLocaleString()
                              : '—'}
                            {job.actual_end_time
                              ? ` – ${new Date(job.actual_end_time).toLocaleString()}`
                              : ''}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <MapPinIcon className="w-4 h-4" /> Customer & Property
                      </h3>
                      <p className="text-sm text-gray-900">
                        {job.customer?.company_name ||
                          `${job.customer?.contact_first_name || ''} ${job.customer?.contact_last_name || ''}`.trim()}
                      </p>
                      <p className="text-sm text-gray-600">{job.property?.name}</p>
                      <p className="text-xs text-gray-500">{job.property?.address}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <DollarSignIcon className="w-4 h-4" /> Financial
                      </h3>
                      <p className="text-sm text-gray-700 mb-3">
                        Billing:{' '}
                        <span className="font-medium capitalize">
                          {(job.billing_type || 'direct').replace('_', ' ')}
                        </span>
                      </p>
                      {canSeePricing && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quoted Amount
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.quoted_amount}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                quoted_amount: parseFloat(e.target.value) || 0,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Actual Amount
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.actual_amount}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                actual_amount: parseFloat(e.target.value) || 0,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                      )}
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <UserIcon className="w-4 h-4" /> Assignment
                      </h3>
                      {id && (
                        <JobAssignmentFields
                          jobId={id}
                          crewId={job.assigned_crew_id}
                          employeeId={job.assigned_employee_id}
                          onAssigned={() => fetchJob(id)}
                        />
                      )}
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FileTextIcon className="w-4 h-4" /> Related
                      </h3>
                      {job.contract_id && job.contract && (
                        <button
                          type="button"
                          onClick={() => navigate(`/contracts/details/${job.contract_id}`)}
                          className="text-sm text-blue-600 hover:underline block"
                        >
                          Contract {job.contract.contract_number}
                        </button>
                      )}
                      {job.proposal_id && job.proposal && (
                        <button
                          type="button"
                          onClick={() => navigate(`/proposals/view/${job.proposal_id}`)}
                          className="text-sm text-blue-600 hover:underline block"
                        >
                          Proposal {job.proposal.proposal_number}
                        </button>
                      )}
                      {!job.contract_id && !job.proposal_id && (
                        <p className="text-sm text-gray-500">No related records.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'work' && id && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Work to be done</h3>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter job description"
                    />
                  </div>
                  <ApartmentJobWork
                    jobId={id}
                    serviceType={formData.service_type || job.service_type}
                    editable
                  />
                  <JobTaskChecklist
                    jobId={id}
                    contractId={job.contract_id}
                    serviceType={formData.service_type || job.service_type}
                    showOneOffBadges={!!job.visit_group_id || !!job.is_recurring}
                    generalOnly
                  />
                  {id && (
                    <JobQrRequirementsPicker jobId={id} propertyId={job.property_id} />
                  )}
                </div>
              )}

              {activeTab === 'line_items' && (
                <div className="space-y-4">
                  {jobLineItems.length === 0 ? (
                    <p className="text-sm text-gray-500">No line items on this job.</p>
                  ) : (
                    <>
                      <ul className="space-y-2 lg:hidden">
                        {jobLineItems.map((item) => (
                          <li
                            key={item.id}
                            className="rounded-xl border border-gray-200 p-3 space-y-1"
                          >
                            <p className="text-sm font-medium text-gray-900 break-words">
                              {item.description}
                            </p>
                            <p className="text-xs text-gray-600">
                              Qty:{' '}
                              {item.is_variable ? 'Variable' : item.quantity}
                              {item.unit_type ? ` ${formatUnitTypeLabel(item.unit_type)}` : ''}
                            </p>
                            {canSeePricing && (
                              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600 pt-1">
                                <span>Rate ${Number(item.unit_price).toFixed(2)}</span>
                                <span className="font-medium text-gray-900">
                                  Total ${Number(item.total_price || 0).toFixed(2)}
                                </span>
                                <span className="capitalize">{item.billing_status || 'pending'}</span>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                      <div className="hidden lg:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left font-medium text-gray-500">
                                Description
                              </th>
                              <th className="px-4 py-2 text-left font-medium text-gray-500">Qty</th>
                              {canSeePricing && (
                                <>
                                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                                    Rate
                                  </th>
                                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                                    Total
                                  </th>
                                  <th className="px-4 py-2 text-left font-medium text-gray-500">
                                    Billing
                                  </th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {jobLineItems.map((item) => (
                              <tr key={item.id}>
                                <td className="px-4 py-2">{item.description}</td>
                                <td className="px-4 py-2">
                                  {item.is_variable ? 'Variable' : item.quantity}{' '}
                                  {item.unit_type ? formatUnitTypeLabel(item.unit_type) : ''}
                                </td>
                                {canSeePricing && (
                                  <>
                                    <td className="px-4 py-2">
                                      ${Number(item.unit_price).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-2">
                                      ${Number(item.total_price || 0).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-2 capitalize">
                                      {item.billing_status || 'pending'}
                                    </td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'gallery' && id && <JobGallery jobId={id} />}

              {activeTab === 'notes' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter additional notes..."
                  />
                </div>
              )}

              {activeTab === 'chat' && id && (
                <div className="space-y-8">
                  <JobChatPanel jobId={id} />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Comments</h3>
                    <JobCommentsThread jobId={id} />
                  </div>
                </div>
              )}

              {activeTab === 'activity' && (
                <div className="space-y-3">
                  {activities.length === 0 ? (
                    <p className="text-sm text-gray-500">No activity recorded yet.</p>
                  ) : (
                    activities.map((activity) => (
                      <div
                        key={activity.id}
                        className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0"
                      >
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <FileTextIcon className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">{activity.description}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatActivityMeta(activity)} ·{' '}
                            {activity.activity_type.replace(/_/g, ' ')}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:mt-2 lg:rounded-xl lg:border lg:border-gray-200">
              <button
                type="button"
                onClick={() => navigate(`/jobs/details/${id}`)}
                className={mobile.secondaryBtn}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className={mobile.primaryBtn}
              >
                <SaveIcon className="w-4 h-4" />
                <span className="sm:hidden">{saving ? 'Saving...' : 'Save'}</span>
                <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </form>
        </div>

        {showCompletionModal && id && (
          <JobCompletionModal
            jobId={id}
            isOpen={showCompletionModal}
            onClose={() => setShowCompletionModal(false)}
            onSaved={async () => {
              setShowCompletionModal(false)
              await saveJob()
            }}
          />
        )}
    </MobilePage>
  )
}